import React, { useEffect, useState } from 'react';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { LucideEye, LucidePlus } from 'lucide-react';
import { Loader } from '../../../ui_components/loader';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../UI/sheet';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { updateAccount } from '../../../api/account';
import { toast } from 'sonner';
import { DescriptionSettingsStateType } from '../../../pages/account_settings';
import KravetPlaceholderImage from '../../../assets/images/kravet-image-placeholder.png';
import {
  ORDERING_DESCRIPTION_SETTINGS_ORDER,
  SALES_DESCRIPTION_SETTINGS_ORDER,
  sortDescriptionSettings,
} from '../../../utils/description-settings';

type DescriptionSettingsProps = {
  activeTab: boolean;
  loading: boolean;
  disabled: boolean;
  accountId: any;
  accountIsOwner: boolean;
  accountIsAdmin: boolean;
  descriptionSettings: DescriptionSettingsStateType;
  handleRefetchAccount?(): void;
};

let initialOrderingDescriptionMap = [
  { id: 1, label: 'Supplier', value: 'supplier' },
  { id: 2, label: 'Name', value: 'name' },
  { id: 3, label: 'Item #', value: 'item' },
  { id: 4, label: 'Specifications', parentheses: '(if exist)', value: 'specifications' },
];

let initialSalesDescriptionMap = [
  { id: 1, label: 'Supplier', value: 'supplier' },
  { id: 2, label: 'Customer-Facing Name', value: 'customer-facing-name' },
  { id: 3, label: 'Name', value: 'name' },
  { id: 4, label: 'Item #', value: 'item' },
  { id: 5, label: 'Specifications', parentheses: '(if exist)', value: 'specifications' },
];

const DescriptionSettings = (props: DescriptionSettingsProps) => {
  const {
    activeTab,
    loading,
    disabled,
    accountId,
    accountIsOwner,
    accountIsAdmin,
    descriptionSettings,
    handleRefetchAccount,
  } = props;

  const [saving, setSaving] = useState<boolean>(false);
  const [orderingDescription, setOrderingDescription] = useState<any[]>(
    descriptionSettings?.orderingDescription || []
  );
  const [salesDescriptionCom, setSalesDescriptionCom] = useState<any[]>(
    descriptionSettings?.salesDescriptionCom || []
  );
  const [salesDescriptionWorkroom, setSalesDescriptionWorkroom] = useState<any[]>(
    descriptionSettings?.salesDescriptionWorkroom || []
  );
  const [openExampleForm, setOpenExampleForm] = useState<boolean>(false);

  // Prefill the account description settings
  useEffect(() => {
    setOrderingDescription(descriptionSettings?.orderingDescription || []);
    setSalesDescriptionCom(descriptionSettings?.salesDescriptionCom || []);
    setSalesDescriptionWorkroom(descriptionSettings?.salesDescriptionWorkroom || []);
  }, [descriptionSettings]);

  const handleSaving = (val: boolean) => {
    setSaving(val);
  };

  const handleOrderingDescription = (item: any, name: string) => {
    if (!item) return;
    switch (name) {
      case 'orderingDescription':
        setOrderingDescription((prev) => {
          let itemExist = prev?.some((i: any) => i.id === item.id);
          return itemExist ? prev.filter((f) => f.id !== item.id) : [...prev, item];
        });
        break;
      case 'salesDescriptionCom':
        setSalesDescriptionCom((prev) => {
          let itemExist = prev?.some((i: any) => i.id === item.id);
          return itemExist ? prev.filter((f) => f.id !== item.id) : [...prev, item];
        });
        break;
      case 'salesDescriptionWorkroom':
        setSalesDescriptionWorkroom((prev) => {
          let itemExist = prev?.some((i: any) => i.id === item.id);
          return itemExist ? prev.filter((f) => f.id !== item.id) : [...prev, item];
        });
        break;
      default:
        break;
    }
  };

  const handleSaveDescriptionSettingsTab = async () => {
    handleSaving(true);
    const payload = {
      accountId: accountId,
      targetAccountId: accountId,
      orderingDescription: sortDescriptionSettings(
        orderingDescription,
        ORDERING_DESCRIPTION_SETTINGS_ORDER
      ),
      salesDescriptionCom: sortDescriptionSettings(
        salesDescriptionCom,
        SALES_DESCRIPTION_SETTINGS_ORDER
      ),
      salesDescriptionWorkroom: sortDescriptionSettings(
        salesDescriptionWorkroom,
        SALES_DESCRIPTION_SETTINGS_ORDER
      ),
    };
    const res = await updateAccount(payload);
    if (res) {
      toast('Settings saved', {
        description: 'Account settings have been updated.',
      });
      if (handleRefetchAccount) handleRefetchAccount();
      setTimeout(() => {
        handleSaving(false);
      }, 1000);
    }
  };

  const handleBuildAnExample = (items: any[] = []) => {
    if (items?.length <= 0) return '—';

    let exampleArray: string[] = [];

    if (items?.some((i) => i?.value === 'supplier')) {
      exampleArray.push('Kravet');
    }

    if (items?.some((i) => i?.value === 'customer-facing-name')) {
      exampleArray.push('Navy Patterned Fabric');
    } else if (items?.some((i) => i?.value === 'name')) {
      exampleArray.push('Brunschwig & Fils/ Beaumois Woven- Navy');
    }

    if (items?.some((i) => i?.value === 'item')) {
      exampleArray.push('#8020108.50.0');
    }
    if (items?.some((i) => i?.value === 'specifications')) {
      exampleArray.push('W: 54" / VR: 5" / HR: 6.75"');
    }

    return exampleArray?.length > 0 ? exampleArray?.join(' - ') : '—';
  };

  const sortedOrderingDescription = sortDescriptionSettings(
    orderingDescription,
    ORDERING_DESCRIPTION_SETTINGS_ORDER
  );
  const sortedSalesDescriptionCom = sortDescriptionSettings(
    salesDescriptionCom,
    SALES_DESCRIPTION_SETTINGS_ORDER
  );
  const sortedSalesDescriptionWorkroom = sortDescriptionSettings(
    salesDescriptionWorkroom,
    SALES_DESCRIPTION_SETTINGS_ORDER
  );
  const displayedSalesDescriptionCom = sortedSalesDescriptionCom.filter((item) => {
    if (sortedSalesDescriptionCom.some((entry) => entry?.value === 'customer-facing-name')) {
      return item?.value !== 'name';
    }
    return true;
  });
  const displayedSalesDescriptionWorkroom = sortedSalesDescriptionWorkroom.filter((item) => {
    if (sortedSalesDescriptionWorkroom.some((entry) => entry?.value === 'customer-facing-name')) {
      return item?.value !== 'name';
    }
    return true;
  });

  return (
    <div className={`space-y-6 ${activeTab ? 'block' : 'hidden'}`}>
      {!accountIsOwner && !accountIsAdmin ? (
        <>
          <div className="space-y-2">
            <h3 className="m-0 text-[16px] font-semibold text-foreground">Description Settings</h3>
            <div className="text-sm text-muted-foreground bg-muted/80 border border-border rounded-md px-4 py-3">
              Description Configuration: contact your account owner or admin to make changes. See in{' '}
              <a className="underline text-primary hover:text-primary/80" href={`/team_settings`}>
                Team Settings
              </a>{' '}
              who is your admin and account owner.
            </div>
          </div>

          {sortedOrderingDescription?.length > 0 && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <div>
                <h4 className="m-0 text-sm font-semibold text-foreground">Ordering Description</h4>
                <p className="text-xs text-muted-foreground">
                  Used on POs, requests for quotes, and the Components table
                </p>
              </div>

              <p className="text-sm text-foreground">
                Fields:{' '}
                {sortedOrderingDescription
                  ?.map((i) => i?.label)
                  ?.filter((f) => f)
                  ?.join(', ')}
              </p>

              <p className="text-xs text-muted-foreground">
                Example: {handleBuildAnExample(sortedOrderingDescription)}
              </p>
            </div>
          )}

          {sortedSalesDescriptionCom?.length > 0 && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <div>
                <h4 className="m-0 text-sm font-semibold text-foreground">
                  Sales Description — COM
                </h4>
                <p className="text-xs text-muted-foreground">
                  Used on Estimates for COM components
                </p>
              </div>

              <p className="text-sm text-foreground">
                Fields:{' '}
                {displayedSalesDescriptionCom
                  ?.map((i) => i?.label)
                  ?.filter((f) => f)
                  ?.join(', ')}
              </p>
              <p className="text-xs text-muted-foreground">
                Example: {handleBuildAnExample(sortedSalesDescriptionCom)}
              </p>
            </div>
          )}

          {sortedSalesDescriptionWorkroom?.length > 0 && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <div>
                <h4 className="m-0 text-sm font-semibold text-foreground">
                  Sales Description — Workroom-Provided
                </h4>
                <p className="text-xs text-muted-foreground">
                  Used on Estimates for Workroom-Provided components
                </p>
              </div>

              <p className="text-sm text-foreground">
                Fields:{' '}
                {displayedSalesDescriptionWorkroom
                  ?.map((i) => i?.label)
                  ?.filter((f) => f)
                  ?.join(', ')}
              </p>
              <p className="text-xs text-muted-foreground">
                Example: {handleBuildAnExample(sortedSalesDescriptionWorkroom)}
              </p>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="space-y-2">
            <h3 className="m-0 text-[16px] font-semibold text-foreground">Description Settings</h3>
            <p className="text-sm text-muted-foreground">
              Define which fields are included in the component description for each context. These
              fields come from the Add / Edit My Shop Item form (Supplier, Name, Customer-Facing
              Name, Item #, and Specifications like Width, Vertical & Horizontal Repeat).
            </p>

            <Sheet open={openExampleForm} onOpenChange={setOpenExampleForm}>
              <SheetTrigger asChild>
                <Button
                  data-qa={`description_settings_open_example_form`}
                  disabled={disabled || saving}
                  variant={'outline'}
                  type="button"
                  className="font-medium min-h-[36px]"
                >
                  Open Example Form
                </Button>
              </SheetTrigger>
              <SheetContent className="sm:max-w-[485px]">
                <SheetHeader className="px-[24px] py-[16px]">
                  <SheetTitle className="text-[20px]">Example: Add / Edit My Shop Item</SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto !h-[calc(100%-130px)] px-6 py-4">
                  <div className="space-y-6">
                    <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                      <h3 className="m-0 text-[16px] font-semibold text-foreground">Information</h3>

                      <div className="space-y-2">
                        <Label className="leading-normal">
                          Category <span className="text-destructive">*</span>
                        </Label>
                        <div>
                          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-10 px-4 py-2 w-full justify-between">
                            Fabric
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="leading-normal">
                          Supplier <span className="text-destructive">*</span>
                        </Label>
                        <div>
                          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-10 px-4 py-2 w-full justify-between">
                            Kravet
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="leading-normal">
                          Name <span className="text-destructive">*</span>
                        </Label>
                        <div>
                          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-md text-sm font-normal ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-10 px-4 py-2 w-full justify-between">
                            Brunschwig & Fils/ Beaumois Woven- Navy
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox checked className="cursor-auto border-primary" />
                          <Label className="font-normal">Add Customer-Facing Name</Label>
                        </div>
                        <div>
                          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-md text-sm font-normal ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-10 px-4 py-2 w-full justify-between">
                            Navy Patterned Fabric
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="leading-normal">Item #</Label>
                        <div>
                          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-md text-sm font-normal ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-10 px-4 py-2 w-full justify-between">
                            8020108.50.0
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="leading-normal">Internal Note</Label>
                        <div>
                          <span className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                            e.g. ordering minimums, special handling instructions, or vendor
                            requirements
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="leading-normal">URL / Link</Label>
                        <div>
                          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-md text-sm font-normal ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-10 px-4 py-2 w-full justify-between">
                            https://www.kravet.com/beaumois-woven-navy
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                      <h3 className="m-0 text-[16px] font-semibold text-foreground">
                        Specifications
                      </h3>

                      <div className="space-y-4">
                        <div className="flex gap-[12px]">
                          <div className="space-y-2 flex-1">
                            <Label className="leading-normal">Fabric Width</Label>
                            <div className="relative">
                              <span className="flex justify-end h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right pr-8">
                                54
                              </span>
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                                in
                              </span>
                            </div>
                          </div>
                          <div className="space-y-2 flex-1">
                            <Label className="leading-normal">Vertical Repeat</Label>
                            <div className="relative">
                              <span className="flex justify-end h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right pr-8">
                                5
                              </span>
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                                in
                              </span>
                            </div>
                          </div>
                          <div className="space-y-2 flex-1">
                            <Label className="leading-normal">Horizontal Repeat</Label>
                            <div className="relative">
                              <span className="flex justify-end h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right pr-8">
                                6.75
                              </span>
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                                in
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 pt-1">
                          <Checkbox checked={false} className="cursor-auto border-primary" />
                          <Label className="font-normal">Drop Repeat</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox checked={false} className="cursor-auto border-primary" />
                          <Label className="font-normal">Specialty Fabric Handling</Label>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <h3 className="m-0 text-[16px] font-semibold text-foreground">Pricing</h3>
                      </div>

                      <div className="space-y-2">
                        <Label className="leading-normal">Method</Label>
                        <div className="flex flex-wrap items-center gap-6">
                          <RadioGroup
                            className="flex flex-wrap items-center gap-6"
                            value={'markup-cost'}
                          >
                            <div className="flex items-center gap-2">
                              <RadioGroupItem className="cursor-auto" value="markup-cost" />
                              <Label className="font-normal">Markup Cost</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <RadioGroupItem className="cursor-auto" value="specify-price" />
                              <Label className="font-normal">Specify Price</Label>
                            </div>
                          </RadioGroup>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="leading-normal">Cost</Label>
                        <div className="relative w-40">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                            $
                          </span>
                          <span className="flex justify-end h-10 w-full rounded-md border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right pl-7 bg-background">
                            45.50
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="leading-normal">Retail Markup</Label>
                          <div className="relative">
                            <span className="flex justify-end h-10 w-full rounded-md border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right pr-7 bg-background">
                              100.00
                            </span>
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                              %
                            </span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="leading-normal">Trade Markup</Label>
                          <div className="relative">
                            <span className="flex justify-end h-10 w-full rounded-md border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right pr-7 bg-background">
                              30.00
                            </span>
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                              %
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="leading-normal">Retail Price</Label>
                          <div className="h-10 px-3 flex items-center text-left bg-muted/30 rounded-md text-sm font-medium border border-transparent">
                            $91.00
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="leading-normal">Trade Price</Label>
                          <div className="h-10 px-3 flex items-center text-left bg-muted/30 rounded-md text-sm font-medium border border-transparent">
                            $59.15
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 pt-1">
                        <div className="flex items-center space-x-2">
                          <Checkbox checked={false} className="cursor-auto border-primary" />
                          <Label className="font-normal">Include Shipping & Handling</Label>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                      <h3 className="m-0 text-[16px] font-semibold text-foreground">Image</h3>
                      <div className="flex items-start gap-3">
                        <div className="w-[126px] h-[126px] rounded-lg border border-input overflow-hidden bg-muted shrink-0">
                          <img
                            className="w-full h-full object-cover"
                            src={KravetPlaceholderImage}
                            alt="Kravet image"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <LucideEye className="lucide lucide-eye w-4 h-4 text-muted-foreground" />
                        <h3 className="m-0 text-[16px] font-semibold text-foreground">
                          Description Preview
                        </h3>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Previews are based on your{' '}
                        <span className="underline text-primary">Description Settings</span> and
                        current values.
                      </p>
                      <div className="space-y-3">
                        <div className="space-y-0.5">
                          <span className="text-xs font-medium text-muted-foreground">
                            Ordering Description
                          </span>
                          <p className="text-sm text-foreground">
                            Brunschwig & Fils/ Beaumois Woven- Navy
                          </p>
                        </div>
                        <div className="space-y-0.5">
                          <span className="text-xs font-medium text-muted-foreground">
                            Sales Description — COM
                          </span>
                          <p className="text-sm text-foreground">
                            Kravet - Navy Patterned Fabric - #8020108.50.0 - W: 54" / VR: 5" / HR:
                            6.75"
                          </p>
                        </div>
                        <div className="space-y-0.5">
                          <span className="text-xs font-medium text-muted-foreground">
                            Sales Description — Workroom-Provided
                          </span>
                          <p className="text-sm text-foreground">
                            Kravet - Brunschwig &amp; Fils/ Beaumois Woven- Navy - #8020108.50.0 -
                            W: 54" / VR: 5" / HR: 6.75"
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <SheetFooter className="h-[71px] sticky bottom-0 z-10 bg-background border-t border-input px-6 py-4"></SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <div>
              <h4 className="m-0 text-sm font-semibold text-foreground">Ordering Description</h4>
              <p className="text-xs text-muted-foreground">
                Used on POs, requests for quotes, and the Components table
              </p>
            </div>

            <div className="flex items-center flex-wrap gap-1">
              {initialOrderingDescriptionMap?.map((item: any, index: number) => {
                return (
                  <div key={item.id} className="flex items-center gap-1">
                    {initialOrderingDescriptionMap?.length > 1 && index !== 0 ? (
                      <LucidePlus className="lucide lucide-plus h-[14px] w-[14px] text-muted-foreground shrink-0" />
                    ) : null}

                    <Label
                      data-disabled={disabled || saving}
                      htmlFor={`ordering_description_${item?.value}_${item?.id}`}
                      className="font-normal flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 bg-background transition-colors data-[disabled=false]:cursor-pointer hover:bg-accent/50 data-[disabled=true]:cursor-not-allowed"
                    >
                      <Checkbox
                        data-qa={`orderingDescription_checkbox_item_${item?.value}`}
                        id={`ordering_description_${item?.value}_${item?.id}`}
                        disabled={disabled || saving}
                        onCheckedChange={() =>
                          handleOrderingDescription(item, 'orderingDescription')
                        }
                        checked={orderingDescription?.some((i) => i?.id === item?.id)}
                      />
                      <span className="text-sm text-foreground whitespace-nowrap">
                        {item?.label || ''}
                        {item?.parentheses ? (
                          <span className="text-xs text-muted-foreground">{` ${item?.parentheses}`}</span>
                        ) : null}
                      </span>
                    </Label>
                  </div>
                );
              })}
            </div>

            <div className="text-xs text-muted-foreground">
              Example: {handleBuildAnExample(sortedOrderingDescription)}
            </div>
          </div>

          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <div>
              <h4 className="m-0 text-sm font-semibold text-foreground">Sales Description — COM</h4>
              <p className="text-xs text-muted-foreground">Used on Estimates for COM components</p>
            </div>

            <div className="flex items-center flex-wrap gap-1">
              {initialSalesDescriptionMap?.map((item: any, index: number) => {
                return (
                  <div key={item.id} className="flex items-center gap-1">
                    {initialSalesDescriptionMap?.length > 1 ? (
                      index !== 0 && index !== 2 ? (
                        <LucidePlus className="lucide lucide-plus h-[14px] w-[14px] text-muted-foreground shrink-0" />
                      ) : index === 2 ? (
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          if provided, otherwise
                        </span>
                      ) : null
                    ) : null}

                    <Label
                      data-disabled={disabled || saving}
                      htmlFor={`sales_description_com_${item?.value}_${item?.id}`}
                      className="font-normal flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 bg-background transition-colors data-[disabled=false]:cursor-pointer hover:bg-accent/50 data-[disabled=true]:cursor-not-allowed"
                    >
                      <Checkbox
                        data-qa={`salesDescriptionCom_checkbox_item_${item?.value}`}
                        id={`sales_description_com_${item?.value}_${item?.id}`}
                        disabled={disabled || saving}
                        onCheckedChange={() =>
                          handleOrderingDescription(item, 'salesDescriptionCom')
                        }
                        checked={salesDescriptionCom?.some((i) => i?.id === item?.id)}
                      />
                      <span className="text-sm text-foreground whitespace-nowrap">
                        {item?.label || ''}
                        {item?.parentheses ? (
                          <span className="text-xs text-muted-foreground">{` ${item?.parentheses}`}</span>
                        ) : null}
                      </span>
                    </Label>
                  </div>
                );
              })}
            </div>

            <div className="text-xs text-muted-foreground">
              Example: {handleBuildAnExample(sortedSalesDescriptionCom)}
            </div>
          </div>

          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <h4 className="m-0 text-sm font-semibold text-foreground">
              Sales Description — Workroom-Provided
            </h4>
            <p className="text-xs text-muted-foreground">
              Used on Estimates for Workroom-Provided components
            </p>

            <div className="flex items-center flex-wrap gap-1">
              {initialSalesDescriptionMap?.map((item: any, index: number) => {
                return (
                  <div key={item.id} className="flex items-center gap-1">
                    {initialSalesDescriptionMap?.length > 1 ? (
                      index !== 0 && index !== 2 ? (
                        <LucidePlus className="lucide lucide-plus h-[14px] w-[14px] text-muted-foreground shrink-0" />
                      ) : index === 2 ? (
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          if provided, otherwise
                        </span>
                      ) : null
                    ) : null}

                    <Label
                      data-disabled={disabled || saving}
                      htmlFor={`sales_description_wo_${item?.value}_${item?.id}`}
                      className="font-normal flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 bg-background transition-colors data-[disabled=false]:cursor-pointer hover:bg-accent/50 data-[disabled=true]:cursor-not-allowed"
                    >
                      <Checkbox
                        data-qa={`salesDescriptionWorkroom_checkbox_item_${item?.value}`}
                        id={`sales_description_wo_${item?.value}_${item?.id}`}
                        disabled={disabled || saving}
                        onCheckedChange={() =>
                          handleOrderingDescription(item, 'salesDescriptionWorkroom')
                        }
                        checked={salesDescriptionWorkroom?.some((i) => i?.id === item?.id)}
                      />
                      <span className="text-sm text-foreground whitespace-nowrap">
                        {item?.label || ''}
                        {item?.parentheses ? (
                          <span className="text-xs text-muted-foreground">{` ${item?.parentheses}`}</span>
                        ) : null}
                      </span>
                    </Label>
                  </div>
                );
              })}
            </div>

            <div className="text-xs text-muted-foreground">
              Example: {handleBuildAnExample(sortedSalesDescriptionWorkroom)}
            </div>
          </div>
          {/* Actions */}
          {!disabled && (
            <div className="flex justify-end">
              <Button
                data-qa={`save_description_settings_tab_button`}
                disabled={disabled || saving}
                onClick={() => handleSaveDescriptionSettingsTab()}
                type="button"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          )}
        </>
      )}

      {(loading || saving) && (
        <Loader className="fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2" />
      )}
    </div>
  );
};

export default DescriptionSettings;
